import get from 'lodash/get';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Lock } from 'react-feather';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'reducer';

// Constants.
import { COMMUNITY_LOCKED_QNS_URL } from 'constants/app';
import { CommentSortBy, CommentSortDir } from 'entity/comment/commentTypes';
import useIsNativeApp from 'utils/hooks/useIsNativeApp';
import AdSlot from 'app/components/misc/AdSlot';
import ArticleCard from 'components/blog/ArticleCard';
import PostCommentProvider from 'components/comment/CommentContext/PostCommentContext';
import CommentList from 'components/comment/CommentList';
import PostCommentForm from 'components/comment/PostCommentForm';
import PostItem from 'components/post/PostItem';
import RelatedProductList from 'components/product/RelatedProductList';

import { addLinksToArticle } from 'entity/blog/blogUtils';
import { useGetPaginationByType } from 'entity/comment/commentHooks';
import { getCommentsRequest } from 'entity/comment/commentRedux';
import { Post } from 'entity/post/postTypes';
import { useIsSignedIn } from 'entity/user/userHooks';
import { usePromptGroupModal } from 'entity/group/groupHooks';
import { openLoginModal } from 'dispatcher/modalDispatcher';

import DropdownSelect from 'app/seedly-component-library/dropdown-select';
import isEmpty from 'lodash/isEmpty';
import { useRouter } from 'next/router';
import { generateCommentLink } from 'utils/generateShareLink';
import getAuthHeaders from 'utils/getAuthHeaders';

import MergedPostCard from './MergedPostCard';
import RelatedPostList from './RelatedPostList';

interface OwnProps {
  post: Post;
}

const PostsContent = (props: OwnProps) => {
  const { post } = props;

  const [sortParam, setSortParam] = useState<{
    by: CommentSortBy;
    dir: CommentSortDir;
  }>({
    by: 'updated_at',
    dir: 'desc',
  });

  const isSignedIn = useIsSignedIn();
  const similarPosts = useSelector(
    (state: RootState) => state.post.similarPosts,
  );

  const router = useRouter();
  const { query } = router;
  const { openlogin } = query;
  const paginationById = useGetPaginationByType('Post', post.id);
  const isNativeApp = useIsNativeApp();
  const authHeader = getAuthHeaders();
  const dispatch = useDispatch();

  const isChildQuestion = post.isMerged && post.isChild;
  const relatedProducts = get(post, 'products', []);
  const isLock = get(post, 'post.status') === 'lock';
  const parentPost = get(post, 'parentQuestion', null);
  const { pagination } = paginationById;
  const isPromoCodeGroup = post.group?.slug === 'promo-codes';

  const handleSortSelect = (selectedSortType: CommentSortBy) => {
    if (selectedSortType !== sortParam.by) {
      dispatch(
        getCommentsRequest({
          commentableType: 'Post',
          commentableId: post.id,
          page: 1,
          sort: {
            by: selectedSortType,
            dir: 'desc',
          },
          shuffleComment: isPromoCodeGroup,
          isRefresh: true,
        }),
      );
      setSortParam({
        by: selectedSortType,
        dir: 'desc',
      });
    }
  };

  const sortOptions = [
    {
      title: 'Most Recent',
      value: 'most-recent',
      onClick: () => handleSortSelect('updated_at'),
    },
    {
      title: 'Most Upvote',
      value: 'most-upvote',
      onClick: () => handleSortSelect('cached_votes_score'),
    },
  ];

  // Refs
  const scrollToComponent = useRef(null);
  const postCommentFormSection = useRef(null);

  // Effects
  useEffect(() => {
    scrollToComponent.current = require('react-scroll-to-component');
  }, []);

  usePromptGroupModal();

  useEffect(() => {
    if (openlogin && openlogin === 'true' && isEmpty(authHeader)) {
      dispatch(openLoginModal());
    }
  }, []);

  const relatedArticles = useMemo(() => {
    const articles = get(post, 'relatedArticles', []);
    return articles.map(article => addLinksToArticle(article));
  }, [post]);

  return (
    <div className="flex flex-col w-full lg:mt-3 mt-0 ">
      <AdSlot type="post" slotKey="leaderboard" />
      <div className="flex flex-row w-full justify-center">
        <div className="flex flex-col items-center w-full lg:w-[644px] gap-6 ">
          <div className="flex flex-col items-center w-full gap-2">
            <PostItem post={post} enableTitleAsLink={false} />
            {isLock && !isChildQuestion && (
              <div className="bg-white rounded p-4 w-full h-fit">
                <div className="flex items-center gap-2">
                  <Lock className="text-blue-500 m-w-[16px]" size={14} />
                  <p className="text-xs">
                    This post is no longer accepting new comments because it is
                    locked.{' '}
                    <a
                      className="text-blue-500 text-xs "
                      href={COMMUNITY_LOCKED_QNS_URL}
                    >
                      What does this mean?
                    </a>
                  </p>
                </div>
              </div>
            )}
            {isChildQuestion && parentPost && (
              <MergedPostCard parentPost={parentPost} />
            )}
          </div>
          <div className="flex flex-col items-center gap-2 w-full">
            <div className="flex items-center pl-4 w-full justify-between">
              <p className="text-base font-bold">
                Discussion ({post.discussionCommentsCount})
              </p>
              {isSignedIn && !isPromoCodeGroup && (
                <div className="flex items-center gap-4">
                  <p>Sort by</p>
                  <DropdownSelect
                    items={sortOptions}
                    defaultSelectedItem={sortOptions[0]}
                    handleSelectedItemchange={item => {
                      item.selectedItem.onClick();
                    }}
                    variant="white"
                  />
                </div>
              )}
            </div>
            <div className="flex flex-col items-center w-full">
              <div ref={postCommentFormSection} />
              <div className="bg-white rounded p-4 w-full ">
                <div className="flex flex-col items-center gap-6 w-full">
                  {!isLock && !isChildQuestion && (
                    <PostCommentProvider post={post}>
                      <PostCommentForm
                        commentableType="Post"
                        commentableId={post.id}
                        expandPostCommentForm
                      />
                    </PostCommentProvider>
                  )}
                  <div className="horizontal-divider" />
                  <PostCommentProvider post={post}>
                    <CommentList
                      totalCommentCount={pagination ? pagination.totalCount : 0}
                      commentableId={post.id}
                      commentableType="Post"
                      childCommentableType="PostComment"
                      commentShareLink={generateCommentLink(post.slug)}
                      data-testid="comment-item"
                      sort={sortParam}
                    />
                  </PostCommentProvider>
                  <div
                    className="rounded bg-green-100 w-full p-3 cursor-pointer"
                    onClick={() => {
                      scrollToComponent.current(
                        postCommentFormSection.current,
                        {
                          duration: 500,
                          align: 'top',
                          offset: -130,
                        },
                      );
                    }}
                  >
                    <p className="text-sm font-bold text-center text-green-600">
                      Write your thoughts
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {relatedArticles.length > 0 && !isNativeApp && (
            <div className="bg-white rounded p-3 h-fit w-full">
              <div className="flex flex-col w-full gap-4">
                <p className="text-base font-bold">Related Articles</p>
                <div className="flex gap-1 items-center w-full overflow-auto">
                  {relatedArticles.map((article, i) => {
                    return (
                      <div key={i}>
                        <ArticleCard {...article} />
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
          {!isNativeApp && (
            <div className="mobile-only max-h-full">
              <RelatedPostList posts={similarPosts} />
            </div>
          )}
        </div>
        <div className="desktop-only flex flex-col gap-4 ml-6 w-[422px]">
          <RelatedProductList products={relatedProducts} />
          <RelatedPostList posts={similarPosts} />
        </div>
      </div>
      <AdSlot type="post" slotKey="bottom-leaderboard" />
    </div>
  );
};

export default PostsContent;
